import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Autocomplete,
  Collapse,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const PCP_OPTIONS_RAW = [
  { id: 32, name: "PEZZOTTI, REYNALDO PEZZOTTI(SPANISH & ENG)" },
  { id: 33, name: "MOLINA-CHUY, YANELA (SPANISH & ENG)" },
  { id: 34, name: "JOHN EDMOND (CREOLE & ENG)" },
  { id: 35, name: "SIDDIKI, AWAIS ( HINDU & ENG)" },
  { id: 36, name: "NAVARO, NELIUDIS (SPANISH & ENG)" },
  { id: 37, name: "GASPARD, RICARDO (CREOLE & ENG)" },
  { id: 38, name: "JEAN MESIDOR" },
  { id: 39, name: "MOYSE, JOSEPH (SPANISH,CREOLE,ENG)" },
  { id: 40, name: "GASPARD, RICARDO (CREOLE & ENG)" },
  { id: 41, name: "SMITH WANDA (SPANISH & ENG)" },
  { id: 42, name: "ROBERT, CHARLES (CREOLE & ENG)" },
  { id: 43, name: "AWAIS SIDDIKI" },
  { id: 44, name: "PORRAS, BELKYS (SPANISH & ENG)" },
  { id: 45, name: "NAVARO, NELIUDIS (SPANISH & ENG)" },
  { id: 46, name: "ROBERT, CHARLES (CREOLE & ENG)" },
  { id: 47, name: "PEZZOTTI, REYNALDO PEZZOTTI(SPANISH & ENG)" },
  { id: 48, name: "PEZZOTTI, REYNALDO PEZZOTTI(SPANISH & ENG)" },
  { id: 49, name: "DHILLON-GILL, RAMANDEEP (HINDU &ENGLISH)" },
  { id: 50, name: "ROBERT SADATY (ENGLISH)" },
  { id: 51, name: "JOLIVAIN, ANN (CREOLE & ENGLISH)" },
  { id: 52, name: "PENA SANCHEZ, ORLY ( SPANISH & ENG)" },
  { id: 53, name: "LOUIS JEAN MARTELLY, YOLETTE (CREOLE & ENG)" },
  { id: 54, name: "RAKIA HAMILTON (ENGLISH ONLY)" },
  { id: 55, name: "YOHANNA DENO (SPANISH, ENGLISH)" },
  { id: 56, name: "RAKIA HAMILTON (ENGLISH ONLY)" },
  { id: 57, name: "LOUIS JEAN MARTELLY, YOLETTE (CREOLE & ENG)" },
  { id: 58, name: "PENA SANCHEZ, ORLY ( SPANISH & ENG)" },
  { id: 59, name: "PENA SANCHEZ, ORLY ( SPANISH & ENG)" },
  { id: 60, name: "ALCIN, ANEILLA (CREOLE & ENG)" },
  { id: 61, name: "VIVIANA PERDOMO (SPANISH & ENGLISH)" },
  { id: 62, name: "RENELIEN, JEAN L (CREOLE & ENG)" },
];

// Dedup por id (por si vienen repetidos en el listado)
const PCP_OPTIONS = PCP_OPTIONS_RAW.reduce((acc, item) => {
  if (!acc.some((x) => x.id === item.id)) acc.push(item);
  return acc;
}, []);

const LOCATION_OPTIONS = [
  { id: 1, name: "FortMyers" },
  { id: 2, name: "FortPierce" },
  { id: 3, name: "Immokalee" },
  { id: 4, name: "Lehigh" },
  { id: 5, name: "Naples" },
  { id: 6, name: "LakeWorth" },
  { id: 7, name: "PortSaintLucie" },
  { id: 8, name: "Unassigned" },
];

// Patient Status: 0 = Active, 1 = Inactive
const PATIENT_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "0", label: "Active" },
  { value: "1", label: "Inactive" },
];

// Workflow Status options
const WORKFLOW_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "0", label: "Pending" },
  { value: "1", label: "In Progress" },
  { value: "2", label: "Completed" },
  { value: "3", label: "Rejected" },
];

// Network options (based on common networks)
const NETWORK_OPTIONS = [
  { value: "", label: "All Networks" },
  { value: "BlueMedicare HMO Plus", label: "BlueMedicare HMO Plus" },
  { value: "BlueMedicare PPO", label: "BlueMedicare PPO" },
  { value: "ACA Marketplace", label: "ACA Marketplace" },
];

export default function PatientsFilters({ value, onChange, onReset }) {
  const [draft, setDraft] = useState(value || {});
  const [pcpOption, setPcpOption] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setDraft(value || {});
  }, [value]);

  // Mantener seleccionado el PCP por pcpId
  useEffect(() => {
    const pid = value?.pcpId;
    if (!pid) {
      setPcpOption(null);
      return;
    }
    const found = PCP_OPTIONS.find((x) => String(x.id) === String(pid));
    setPcpOption(found || null);
  }, [value?.pcpId]);

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleApply = () => {
    // Convert dob from dayjs to string if needed
    const finalDraft = { ...draft };
    if (finalDraft.dob && dayjs.isDayjs(finalDraft.dob)) {
      finalDraft.dob = finalDraft.dob.format("YYYY-MM-DD");
    }
    onChange?.((prev) => ({
      ...prev,
      ...finalDraft,
      page: 1,
    }));
  };

  const handleReset = () => onReset?.();

  const memberTypeOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "ACA", label: "ACA" },
      { value: "MED", label: "MED" },
    ],
    []
  );

  // Count active filters (excluding page/pageSize)
  const activeFilterCount = useMemo(() => {
    const keys = ["pcpId", "memberType", "patientName", "dob", "patientStatus", "patientWorkflowStatus", "locationId", "networkName", "memberPlan"];
    return keys.filter((k) => draft[k] && draft[k] !== "").length;
  }, [draft]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Primary Filters Row */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap" alignItems="flex-start">
          {/* Patient Name */}
          <TextField
            label="Patient Name"
            value={draft?.patientName || ""}
            onChange={(e) => set({ patientName: e.target.value })}
            size="small"
            placeholder="Search by name..."
            sx={{ minWidth: 200 }}
          />

          {/* DOB */}
          <DatePicker
            label="Date of Birth"
            value={draft?.dob ? dayjs(draft.dob) : null}
            onChange={(newValue) => set({ dob: newValue })}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 160 } },
            }}
          />

          {/* Patient Status */}
          <TextField
            label="Patient Status"
            value={draft?.patientStatus ?? ""}
            onChange={(e) => set({ patientStatus: e.target.value })}
            size="small"
            select
            sx={{ minWidth: 140 }}
          >
            {PATIENT_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Workflow Status */}
          <TextField
            label="Workflow Status"
            value={draft?.patientWorkflowStatus ?? ""}
            onChange={(e) => set({ patientWorkflowStatus: e.target.value })}
            size="small"
            select
            sx={{ minWidth: 150 }}
          >
            {WORKFLOW_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Location dropdown */}
          <TextField
            label="Location"
            value={draft?.locationId || ""}
            onChange={(e) => set({ locationId: e.target.value })}
            size="small"
            select
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Locations</MenuItem>
            {LOCATION_OPTIONS.map((loc) => (
              <MenuItem key={loc.id} value={String(loc.id)}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              endIcon={<FilterIcon />}
              sx={{ textTransform: "none" }}
            >
              {showAdvanced ? "Less" : "More"} Filters
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    color: "white",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Button>
            <Divider orientation="vertical" flexItem />
            <Button variant="contained" onClick={handleApply} size="small">
              Apply
            </Button>
            <Button variant="outlined" onClick={handleReset} size="small">
              Reset
            </Button>
          </Stack>
        </Stack>

        {/* Advanced Filters */}
        <Collapse in={showAdvanced}>
          <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: "grey.50" }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Additional Filters
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
              {/* PCP Autocomplete */}
              <Autocomplete
                options={PCP_OPTIONS}
                value={pcpOption}
                onChange={(_, opt) => {
                  setPcpOption(opt);
                  set({ pcpId: opt ? String(opt.id) : "" });
                }}
                getOptionLabel={(opt) => (opt ? `${opt.id} - ${opt.name}` : "")}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="PCP"
                    size="small"
                    placeholder="Search PCP..."
                  />
                )}
                sx={{ minWidth: 300 }}
              />

              {/* Member Type */}
              <TextField
                label="Member Type"
                value={draft?.memberType || ""}
                onChange={(e) => set({ memberType: e.target.value })}
                size="small"
                select
                sx={{ minWidth: 140 }}
              >
                {memberTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Network */}
              <TextField
                label="Network"
                value={draft?.networkName || ""}
                onChange={(e) => set({ networkName: e.target.value })}
                size="small"
                select
                sx={{ minWidth: 200 }}
              >
                {NETWORK_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Member Plan */}
              <TextField
                label="Member Plan"
                value={draft?.memberPlan || ""}
                onChange={(e) => set({ memberPlan: e.target.value })}
                size="small"
                placeholder="Search plan..."
                sx={{ minWidth: 200 }}
              />
            </Stack>
          </Paper>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
